import { LISCase, PatientInsurance } from "pwapi";

import {
  ADMIN_DELETE_CASE_DOCUMENT_SUCCESS,
  ADMIN_UPLOAD_CASE_DOCUMENT_SUCCESS,
} from "shared/actions/adminConstants";
import { CASE_WITH_WORK_ACTIONS, LIS } from "shared/actions/constants";
import { PWAction } from "shared/utils/redux";

import { ACCESSION_CASE_SUCCESS } from "app/actions/caseActions";

import {
  CREATE_LIS_SLIDE_SUCCESS,
  CREATE_LIS_SPECIMEN_SUCCESS,
  DELETE_LIS_SPECIMEN_SUCCESS,
  UPDATE_LIS_CASE_REQUEST,
  UPDATE_LIS_CASE_SUCCESS,
  UPDATE_LIS_INSURANCE_SUCCESS,
  UPDATE_LIS_PATIENT_SUCCESS,
  UPDATE_LIS_SPECIMEN_SUCCESS,
} from "./admin/constants";

export type CaseState = Omit<LISCase, "_meta"> | null;

const caseReducer = (state: CaseState = null, action: PWAction): CaseState => {
  let json: any, _meta: any;
  if (CASE_WITH_WORK_ACTIONS.includes(action.type)) {
    ({ _meta, ...json } = action.payload.response);
    return json;
  }

  switch (action.type) {
    case LIS.ACCESSION.PRINT.SUCCESS:
      ({ _meta, ...json } = action.payload.response.created_case);
      if (state?.id && state.id === json.id) return json;
      return state;
    case LIS.SPECIMEN.UPDATE.SUCCESS:
      json = action.payload.response;
      if (!json.case_id || !state || json.case_id !== state.id) return state;
      return {
        ...state,
        specimens: [...state.specimens.filter((s) => s.id !== json.id), json],
      };
    case ACCESSION_CASE_SUCCESS:
      return {
        ...state,
        ...action.payload.response.case, // TODO: return just a case here? not the work_process
      };
    // Optimistically update the case with the new patched information only
    case UPDATE_LIS_CASE_REQUEST:
      return (
        state && {
          ...state,
          ...action.args,
        }
      );

    // Update only the fields that were patched, do not update other unnecessary things
    // This is only used for mini patching outside of the LIS Case Editor
    case UPDATE_LIS_CASE_SUCCESS:
      const changedFields = Object.keys(action.args).reduce(
        (acc: { [key: string]: any }, key) => {
          return { ...acc, [key]: action.payload.response[key] };
        },
        {}
      );
      return (
        state && {
          ...state,
          ...changedFields,
        }
      );

    case UPDATE_LIS_PATIENT_SUCCESS:
      const changedPatientFields = state?.patient
        ? Object.keys(state.patient).reduce(
            (acc: { [key: string]: any }, key: any) => {
              return state.patient[key as keyof LISCase["patient"]] !==
                action.payload.response[key]
                ? { ...acc, [key]: action.payload.response[key] }
                : acc;
            },
            {}
          )
        : {};
      return (
        state && {
          ...state,
          patient: {
            ...state.patient,
            ...changedPatientFields,
          },
        }
      );

    case UPDATE_LIS_INSURANCE_SUCCESS:
      const index = state?.patient?.insurance.length
        ? state.patient.insurance.findIndex(
            (insurance) => insurance.id === action.payload.response.id
          )
        : -1;
      return (
        state && {
          ...state,
          patient: {
            ...state.patient,
            insurance:
              state?.patient?.insurance?.length && index > -1
                ? [
                    ...state.patient.insurance.slice(0, index),
                    action.payload.response as PatientInsurance,
                    ...state.patient.insurance.slice(index + 1),
                  ]
                : state.patient.insurance,
          },
        }
      );

    case UPDATE_LIS_SPECIMEN_SUCCESS:
      return state?.specimens && action.payload.response?.id
        ? {
            ...state,
            specimens: state.specimens.map((s) => {
              return s.id === action.payload.response.id
                ? action.payload.response
                : s;
            }),
          }
        : state;

    case CREATE_LIS_SPECIMEN_SUCCESS:
      return state?.specimens && action.payload.response
        ? {
            ...state,
            specimens: [...state?.specimens, action.payload.response],
          }
        : state;

    case DELETE_LIS_SPECIMEN_SUCCESS:
      const specimenDelIndex = state?.specimens?.findIndex(
        (s) => s.id === action.args.id
      );

      return state?.specimens && specimenDelIndex && specimenDelIndex > -1
        ? {
            ...state,
            specimens: [
              ...state.specimens.slice(0, specimenDelIndex),
              ...state.specimens.slice(specimenDelIndex + 1),
            ],
          }
        : state;

    case LIS.BLOCK.UPDATE.SUCCESS:
      //This is not an LIS specific call so it will not have a cassette_printing_task_id
      //TODO: make this an LIS specific call

      return state?.blocks && action.payload.response.id
        ? {
            ...state,
            blocks: state.blocks.map((b) => {
              return b.id === action.payload.response.id
                ? {
                    ...b,
                    piece_count: action.payload.response.piece_count,
                  }
                : b;
            }),
          }
        : state;
    case CREATE_LIS_SLIDE_SUCCESS:
      const slide = action.payload.response;
      return state && state?.id === slide.case_id
        ? {
            ...state,
            slides: [...state.slides, slide],
          }
        : state;

    case ADMIN_UPLOAD_CASE_DOCUMENT_SUCCESS:
      return state && state.id === action.payload.case_id
        ? {
            ...state,
            documents: [...(state.documents || []), action.payload],
          }
        : state;

    case ADMIN_DELETE_CASE_DOCUMENT_SUCCESS:
      return state
        ? {
            ...state,
            documents: state.documents?.filter(
              (document) => document.id !== action.payload.id
            ),
          }
        : state;

    case LIS.CASE.GET.ERROR:
      return null;
  }
  return state;
};

export default caseReducer;
